/**
 * Extracted navigation/decision logic from LcForm.
 * All state is accessed via refs from the component.
 *
 * @param {Object} deps
 * @param {import('vue').Ref} deps.app - ref
 * @param {Object} deps.applicationSettings - reactive (writable computed)
 * @param {Function} deps.persistLocally
 * @param {Function} deps.formData - getter function for formData prop
 * @param {import('vue').Ref} deps.activeStepIndex - ref
 * @param {import('vue').Ref} deps.activeStep - ref
 * @param {import('vue').Ref} deps.shouldSkipNextStep - ref
 * @param {Function} deps.emit - component emit function
 * @param {Function} deps.validateForm - from validation composable
 * @param {Object} deps.submission - useFormSubmission return object
 * @param {Object} deps.loading - useLoadingAnimation return object
 * @param {Function} deps.getComponentMethod - lookup a method on the component by name
 */
import { clearNotifications } from '@/utils/notifications.js';
import { scrollToTop, scrollToFirstInvalidField } from '@/utils/scroll.js';

export function useFormNavigation(deps) {

    /**
     * Shared helper that resolves and executes a step callback.
     * Handles both function and string callback types, including
     * functions that return a string method name.
     */
    async function executeStepCallback(callback) {
        if (!callback) {
            return;
        }
        if (typeof callback === 'function') {
            const returnedValue = await callback({ ...deps.app.value });
            if (typeof returnedValue === 'string') {
                await invokeComponentMethod(returnedValue);
            }
        } else if (typeof callback === 'string') {
            await invokeComponentMethod(callback);
        }
    }

    /**
     * Looks up a method by name in the component method registry and invokes it.
     * Warns if the method name is not found.
     */
    async function invokeComponentMethod(name) {
        const method = deps.getComponentMethod(name);
        if (typeof method === 'function') {
            await method();
        } else {
            console.warn(`[LcForm] Step callback "${name}" not found in method registry.`);
        }
    }

    async function goToNextStep(shouldSkipValidation = false) {
        const appSettings = deps.applicationSettings;
        const formData = deps.formData();

        // Dev mode: skip validation, callbacks, and partial requests
        if (appSettings._devMode && !appSettings._devAllowRequests) {
            scrollToTop();
            if (appSettings._devForceValidation && !shouldSkipValidation) {
                const validatedForm = await deps.validateForm();
                if (!validatedForm) {
                    return;
                }
            }

            const activeStepIndex = deps.activeStepIndex.value;
            let newIndex = activeStepIndex + 1;
            if (newIndex >= formData.steps.length) {
                alert('[dev_step] End of form steps reached. No submission was made — this was a dev test.');
                return;
            }
            deps.activeStepIndex.value = newIndex;
            deps.activeStep.value = formData.steps[newIndex];
            return;
        }

        // Skip, radio auto-advance and auto-skipped steps still validate, but only a real Next
        // puts error messages on screen.
        const validatedForm = await deps.validateForm({ reveal: !shouldSkipValidation });
        if (!shouldSkipValidation && !validatedForm) {
            // Take the person to the problem, not to the top of the page.
            await scrollToFirstInvalidField();
            return;
        }

        scrollToTop();

        deps.persistLocally(); // [DISABLED] currently a no-op — see useApplicationCore.js

        const activeStepIndex = deps.activeStepIndex.value;

        if (appSettings.partialRequestOnEveryStep && !isLastApplicationStep()) {
            await deps.submission.executeCreatePartialApp();
        }

        // If this is the last real step and onSubmit exists, complete the flow
        if (formData.onSubmit && isLastRealStep()) {
            await executeBeforeNextStep();
            await executeFlowCompletedAfterValidation();
            return;
        }

        await executeBeforeNextStep();
        await executeBeforeCreateNextStep();

        let shouldSkip = deps.shouldSkipNextStep.value;
        let newIndex = shouldSkip ? activeStepIndex + 2 : activeStepIndex + 1;
        deps.activeStepIndex.value = newIndex;
        deps.activeStep.value = formData.steps[newIndex];

        if (!isLastStep()) {
            deps.shouldSkipNextStep.value = false;
        }
    }

    async function goToNextStepFromNestedComponent(shouldSkipValidation = false) {
        const formData = deps.formData();

        if (formData.onSubmit && isLastRealStep()) {
            executeFlowCompleted(shouldSkipValidation);
            return;
        }

        if (isLastStep()) {
            executeFlowCompleted(shouldSkipValidation);
            return;
        }

        goToNextStep(shouldSkipValidation);
    }

    async function executeBeforeNextStep() {
        await executeStepCallback(deps.activeStep.value.beforeNextStep);
    }

    async function executeBeforeCreateNextStep() {
        await executeStepCallback(getNextStep()?.beforeCreate);
    }

    function checkIfLastStep() {
        const formData = deps.formData();
        if (deps.activeStepIndex.value > formData.steps.length - 1) {
            deps.emit("completed", true);
        }
    }

    async function executeFlowCompleted(shouldSkipValidation = false) {
        if (deps.applicationSettings._devMode && !deps.applicationSettings._devAllowRequests) {
            alert('[dev_step] End of form flow reached. No submission was made — this was a dev test.');
            return;
        }

        const validatedForm = await deps.validateForm({ reveal: !shouldSkipValidation });
        if (!shouldSkipValidation && !validatedForm) {
            await scrollToFirstInvalidField();
            return;
        }
        deps.persistLocally(); // [DISABLED] currently a no-op — see useApplicationCore.js

        const formData = deps.formData();
        if (formData.onSubmit === true) {
            await deps.submission.executeSubmitApplication();
        } else {
            await executeBeforeNextStep(); // legacy path for flows without onSubmit
        }

        deps.emit("completed", true);
    }

    /**
     * Submit + emit completed, skipping validation (already validated by caller).
     */
    async function executeFlowCompletedAfterValidation() {
        if (deps.applicationSettings._devMode && !deps.applicationSettings._devAllowRequests) {
            alert('[dev_step] End of form flow reached. No submission was made — this was a dev test.');
            return;
        }

        const formData = deps.formData();
        if (formData.onSubmit === true) {
            await deps.submission.executeSubmitApplication();
        }
        deps.emit("completed", true);
    }

    function executeSkipVerification() {
        const appSettings = deps.applicationSettings;
        clearNotifications(appSettings);
        appSettings.userWantsVerification = false;
    }

    function executeSkipRegistration() {
        const appSettings = deps.applicationSettings;
        clearNotifications(appSettings);
        appSettings.userWantsVerification = false;
        appSettings.userWantsRegistration = false;
    }

    function executeSkipIfAuthenticated() {
        const appSettings = deps.applicationSettings;
        if (appSettings.isUserAuthenticated) {
            goToNextStep(true);
        }
    }

    function executeClearPassword() {
        const appSettings = deps.applicationSettings;
        if (!appSettings.isUserAuthenticated) {
            deps.app.value['password'] = null;
        }
    }

    function handleUserFormInteraction() {
        const appSettings = deps.applicationSettings;
        appSettings.stepOnFirstUserClick = appSettings.stepOnFirstUserClick === null ? appSettings.progressBar.step : appSettings.stepOnFirstUserClick;

        if (!window._userFormInteractionDispatched) {
            window._userFormInteractionDispatched = true;
            window.dispatchEvent(
                new CustomEvent('user-form-interaction', {
                    detail: { type: 'click' }
                })
            );
        }
    }

    function isLastStep() {
        const formData = deps.formData();
        return deps.activeStepIndex.value === formData.steps.length - 1;
    }

    function getNextStep() {
        const formData = deps.formData();
        return formData.steps[deps.activeStepIndex.value + 1] ?? null;
    }

    /**
     * Checks whether all remaining steps after the current one are hidden (via hideIfCondition).
     * Used with onSubmit to determine the real last visible step.
     */
    function isLastRealStep() {
        const formData = deps.formData();
        const currentIndex = deps.activeStepIndex.value;
        for (let i = currentIndex + 1; i < formData.steps.length; i++) {
            const step = formData.steps[i];
            if (typeof step.hideIfCondition !== 'function' || !step.hideIfCondition({ ...deps.app.value })) {
                return false;
            }
        }
        return currentIndex >= 0 && currentIndex <= formData.steps.length - 1;
    }

    function isLastApplicationStep() {
        const formData = deps.formData();
        if (formData.onSubmit) {
            return isLastRealStep();
        }
        return false;
    }

    return {
        goToNextStep,
        goToNextStepFromNestedComponent,
        executeBeforeNextStep,
        executeBeforeCreateNextStep,
        checkIfLastStep,
        executeFlowCompleted,
        executeSkipVerification,
        executeSkipRegistration,
        executeSkipIfAuthenticated,
        executeClearPassword,
        handleUserFormInteraction,
        isLastStep,
        getNextStep,
        isLastRealStep,
        isLastApplicationStep,
    };
}
